import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";

const HEADER_BG = "#374151";
const CARD     = { borderRadius: "8px", marginTop: "16px", width: "fit-content", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" };

function normalCdf(x) {
    const t = 1 / (1 + 0.2316419 * x);
    const pdf = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
    const poly = ((((1.330274429 * t - 1.821255978) * t + 1.781477937) * t - 0.356563782) * t + 0.319381530) * t;
    return 1 - pdf * poly;
}

// Sign test on raw comparison counts
function pairPValue(nij, nji) {
    const n = nij + nji;
    if (n === 0) return 1;
    const z = (nij - n / 2) / Math.sqrt(n / 4);
    return 2 * (1 - normalCdf(Math.abs(z)));
}

function formatP(p) {
    if (p < 0.00001) return "< 0.00001";
    if (p < 0.0001)  return "< 0.0001";
    if (p < 0.001)   return "< 0.001";
    return p.toFixed(4);
}

export default function PairwiseDiagram({ data, modelType }) {
    const wrapperRef   = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!data || !data.twoAfcData) return;

        function render() {
            if (!containerRef.current || !wrapperRef.current) return;

            const totalW = Math.max(300, wrapperRef.current.clientWidth - 32);

            const transformed  = matrixTransformation(data);
            const rawLabels    = transformed.sortedMethods || [];
            const winMatrix    = transformed.matrix;
            const model        = modelType === "BT" ? bradleyTerry(transformed) : thurstoneMosteller(transformed);
            const { betas } = model;
            const N = rawLabels.length;
            if (N < 2) return;

            // Sort by beta ascending
            const order       = rawLabels.map((_, i) => i).sort((a, b) => betas[a] - betas[b]);
            const labels      = order.map(i => rawLabels[i]);
            const sortedBetas = order.map(i => betas[i]);

            // All pairs
            const pairs = [];
            for (let a = 0; a < N; a++) {
                for (let b = a + 1; b < N; b++) {
                    const oi = order[a], oj = order[b];
                    const p    = pairPValue(winMatrix[oi][oj], winMatrix[oj][oi]);
                    const diff = Math.abs(sortedBetas[b] - sortedBetas[a]);
                    pairs.push({ a, b, diff, p, sig: p < 0.05 });
                }
            }

            const CIRCLE_R   = 5;
            const STACK_STEP = 130;
            const X_CLASH    = Math.max(80, totalW * 0.12);

            const margin = { top: 20, right: 40, bottom: 85, left: 60 };
            const innerW = totalW - margin.left - margin.right;

            const betaMin = sortedBetas[0];
            const betaMax = sortedBetas[N - 1];
            const betaSpan = Math.max(betaMax - betaMin, 0.01);
            const pad = betaSpan * 0.35;

            const xScale = d3.scaleLinear()
                .domain([betaMin - pad, betaMax + pad])
                .range([0, innerW]);

            const xPx = sortedBetas.map(b => xScale(b));

            // Beeswarm y-positions
            const MIN_LIFT = 40;

            const rawH     = new Array(N).fill(0);

            for (let i = 0; i < N; i++) {
                let h = 0;
                let changed = true;
                while (changed) {
                    changed = false;
                    for (let j = 0; j < i; j++) {
                        if (Math.abs(xPx[i] - xPx[j]) < X_CLASH &&
                            Math.abs(h - rawH[j]) < STACK_STEP) {
                            h      += STACK_STEP;
                            changed = true;
                            break;
                        }
                    }
                }
                rawH[i] = h;
            }

            const heights  = rawH.map(h => h + MIN_LIFT);

            const xEff = xPx;

            const maxH   = Math.max(...heights, 0);
            const innerH = maxH + CIRCLE_R + 20 + pairs.length * 8;
            const totalH = innerH + margin.top + margin.bottom;

            const yBase   = innerH;
            const cyArr  = heights.map(h => yBase - h);

            // SVG
            const svg = d3.create("svg")
                .attr("width", totalW)
                .attr("height", totalH);

            const g = svg.append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            g.append("g")
                .attr("transform", `translate(0,${yBase})`)
                .call(d3.axisBottom(xScale).ticks(6).tickFormat(d3.format(".2f")))
                .selectAll("text").attr("font-size", "11px");

            g.append("text")
                .attr("x", innerW / 2).attr("y", yBase + 55)
                .attr("text-anchor", "middle")
                .attr("font-size", "12px").attr("fill", "#374151")
                .text("Quality score (β)");

            const LBL_H = 28;

            const lineMap = {};
            [...pairs]
                .sort((a, b) => (a.sig ? 1 : 0) - (b.sig ? 1 : 0))
                .forEach(pair => {
                    const { a, b } = pair;
                    const seg = g.append("line")
                        .attr("x1", xEff[a]).attr("y1", cyArr[a])
                        .attr("x2", xEff[b]).attr("y2", cyArr[b])
                        .attr("stroke", pair.sig ? "#2563eb" : "#dc2626")
                        .attr("stroke-width", pair.sig ? 1.8 : 1.4);
                    if (!pair.sig) seg.attr("stroke-dasharray", "5,3");
                    lineMap[`${a}-${b}`] = seg;
                });

            const edgeLabelMap = {};
            pairs.forEach(pair => {
                const { a, b } = pair;
                const mx = (xEff[a] + xEff[b]) / 2;
                const my = (cyArr[a] + cyArr[b]) / 2;
                const color   = pair.sig ? "#2563eb" : "#dc2626";
                const diffStr = pair.diff.toFixed(2);
                const pStr    = `(${formatP(pair.p)})`;
                const bgW     = Math.max(diffStr.length, pStr.length) * 4.4;

                const grp = g.append("g").attr("transform", `translate(${mx},${my})`);
                grp.append("rect")
                    .attr("x", -bgW / 2).attr("y", -LBL_H / 2)
                    .attr("width", bgW).attr("height", LBL_H)
                    .attr("fill", "white").attr("rx", 2).attr("opacity", 0.97);
                grp.append("text")
                    .attr("x", 0).attr("y", -4)
                    .attr("text-anchor", "middle")
                    .attr("font-size", "10px").attr("font-weight", "700")
                    .attr("fill", color).text(diffStr);
                grp.append("text")
                    .attr("x", 0).attr("y", 8)
                    .attr("text-anchor", "middle")
                    .attr("font-size", "9px").attr("fill", color).text(pStr);
                edgeLabelMap[`${a}-${b}`] = grp;
            });

            labels.forEach((label, si) => {
                const cx = xEff[si];

                const nodeG = g.append("g").attr("cursor", "ns-resize");

                const circle = nodeG.append("circle")
                    .attr("cx", cx).attr("cy", cyArr[si])
                    .attr("r", CIRCLE_R)
                    .attr("fill", "white")
                    .attr("stroke", "#374151").attr("stroke-width", 1.5);

                const labelTxt = nodeG.append("text")
                    .attr("x", cx)
                    .attr("y", cyArr[si] + CIRCLE_R + 14)
                    .attr("text-anchor", "middle")
                    .attr("font-size", "11px").attr("font-weight", "500")
                    .attr("fill", "#374151")
                    .style("stroke", "white").style("stroke-width", "2px")
                    .style("stroke-linejoin", "round").style("paint-order", "stroke")
                    .text(label);

                nodeG.call(
                    d3.drag()
                        .on("start", function () { nodeG.raise(); })
                        .on("drag", function (event) {
                            const newY = Math.max(0, Math.min(yBase, event.y));
                            cyArr[si] = newY;
                            circle.attr("cy", newY);
                            labelTxt.attr("y", newY + CIRCLE_R + 14);

                            pairs.forEach(pair => {
                                if (pair.a !== si && pair.b !== si) return;
                                const key = `${pair.a}-${pair.b}`;
                                lineMap[key]
                                    .attr("y1", cyArr[pair.a])
                                    .attr("y2", cyArr[pair.b]);
                                const mx = (xEff[pair.a] + xEff[pair.b]) / 2;
                                const my = (cyArr[pair.a] + cyArr[pair.b]) / 2;
                                edgeLabelMap[key].attr("transform", `translate(${mx},${my})`);
                            });
                        })
                );
            });

            containerRef.current.innerHTML = "";
            containerRef.current.appendChild(svg.node());
        }

        render();
        let rafId;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(render);
        });
        const el = wrapperRef.current;
        if (el) ro.observe(el);
        return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
    }, [data, modelType]);

    const modelTag = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";

    return (
        <div style={CARD}>
            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>
                    Pairwise Significance Diagram
                </h4>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                Methods plotted by quality score (β). Lines connect every pair, labeled with score difference and p-value.
            </div>

            <div ref={wrapperRef} style={{ ...SECTION, padding: "12px 16px 8px", background: "white" }}>
                <div ref={containerRef} />
            </div>

            <div style={FOOTER}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <svg width="24" height="8" style={{ overflow: "visible" }}>
                        <line x1="0" y1="4" x2="24" y2="4" stroke="#2563eb" strokeWidth="2" />
                    </svg>
                    significant (p &lt; 0.05)
                </span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <svg width="24" height="8" style={{ overflow: "visible" }}>
                        <line x1="0" y1="4" x2="24" y2="4" stroke="#dc2626" strokeWidth="2" strokeDasharray="5,3" />
                    </svg>
                    not significant (p ≥ 0.05)
                </span>
                <span>|</span>
                <span>numbers: score difference (p-value)</span>
            </div>
        </div>
    );
}
